/**
 * Debits Table container logic
 *
 * @author Nuno Gago <ngago@ubiwhere.com>
 *
 */

import { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ThemeContext } from 'styled-components';

import { ITranslations, IPagination } from '@paco_ua/pacoui';
import { checkAreAllFiltersFilled, DEFAULT_STRUCTURE } from './utils';

import { DEFAULT_PAGINATION, IIsolatedOption } from './types';

import * as selectors from './logic/selectors';
import { actions } from 'store/rootSlices';

export default (
  t,
  isolatedOptions: IIsolatedOption[],
  compileTableRow,
  total: number,
  fetchIsolatedOptions,
  filterParams,
  registrationId,
  onSelectRow,
  mandatoryFilters,
  ucs
) => {
  const theme = useContext(ThemeContext);
  const [structure, setStructure] = useState(DEFAULT_STRUCTURE(t));
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION(total));
  const dispatch = useDispatch();

  const isolatedOptionsListSlice = useSelector(selectors.getIsolatedOptionsListSlice);
  const {
      fetchIsolatedOptionsFilters,
    } = actions.IsolatedOptionsTable;

  const [initialFetch, setInitialFetch] = useState(true);

  // initial debits fetch
  useEffect(() => {
    if (initialFetch) {
      setInitialFetch(false);
    } else {
      
      if(
        !checkAreAllFiltersFilled(isolatedOptionsListSlice.filterParams)
      ){
          dispatch(fetchIsolatedOptionsFilters({
            limit: isolatedOptionsListSlice.isolatedOptionsPage.limit,
            offset: 0,
            mandatoryFilters,
            registrationId
          }))
      }
      else{
        fetchIsolatedOptions({
          limit: isolatedOptionsListSlice.isolatedOptionsPage.limit,
          offset: 0,
          mandatoryFilters,
        });
      }
    }
  }, [filterParams]);

  // update pagination when total value is changed (when filtering)
  useEffect(() => {
    let updatePagination = { ...pagination };
    updatePagination.total = total;

    setPagination(updatePagination);
  }, [total]);

  useEffect(() => {
    handlePaginationNavigation(pagination);
  }, [pagination]);

  // Update table when new debits are fetched,
  // or to disable the payment buttons when multiple debits are selected
  useEffect(() => {
    const tableStructure = compileTableStructure(t, isolatedOptions, theme);
    setStructure(tableStructure);
  }, [isolatedOptions]);

  const compileTableStructure = (t: ITranslations, isolatedOptions: IIsolatedOption[], theme) => ({
    header: {
      type: 'columnTitle',
      titles: [
        { text: t('sgh.department', { textOnly: true }) },
        { text: t('sgh.scientificField', { textOnly: true }) },
        { text: t('sgh.code', { textOnly: true }) },
        { text: t('sgh.uc', { textOnly: true }) },
        //{ text: t('sgh.workload', { textOnly: true }) },
        { text: t('sgh.ects', { textOnly: true }) },
        { text: t('sgh.semester', { textOnly: true }) },
        { text: t('sgh.freeSlots', { textOnly: true }) },
        { text: '' },
      ],
    },
    rows: !isolatedOptions.length
      ? ([] as any)
      : [
          ...isolatedOptions.map((isolatedOption) =>
            compileTableRow(isolatedOption, theme, t, registrationId, onSelectRow, dispatch, ucs)
          ),
        ],
  });

  const handlePaginationNavigation = (newPagination: IPagination) => {
    const exceedsTotal = newPagination.limit >= newPagination.total;

    if (exceedsTotal) {
      newPagination.hasPrevious = false;
      newPagination.hasNext = false;
    }

    if (!exceedsTotal && newPagination.offset < newPagination.limit) {
      newPagination.hasPrevious = false;
      newPagination.hasNext = true;
    }

    if (!exceedsTotal && newPagination.offset >= newPagination.limit) {
      newPagination.hasPrevious = true;
      newPagination.hasNext =
        newPagination.offset + newPagination.limit >= newPagination.total ? false : true;
    }

    setPagination(newPagination);
  };

  const onChangeRows = (value: number): void => {
    const newPagination = { ...pagination };
    newPagination.limit = value;
    newPagination.offset = 0;

    handlePaginationNavigation(newPagination);
    setPagination(newPagination);
    fetchIsolatedOptions({ ...newPagination, mandatoryFilters });
  };

  const onChangePage = (value: string): void => {
    const newPagination = { ...pagination };

    switch (value) {
      case 'prev':
        newPagination.offset -= newPagination.limit;
        break;
      case 'next':
        newPagination.offset += newPagination.limit;
        break;
      default:
        break;
    }

    handlePaginationNavigation(newPagination);
    setPagination(newPagination);
    fetchIsolatedOptions({ ...newPagination, mandatoryFilters });
  };

  return {
    theme,
    structure,
    pagination,
    onChangePage,
    onChangeRows,
  };
};
